import React, { useState, useMemo } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import './PastSessionGroups.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlayCircle, faLock, faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons'; // Added faLock
import DOMPurify from 'dompurify';
import { pushEvent, ANALYTICS_EVENTS } from '../../utils/analytics'; // Import analytics

// Utility to extract YouTube video ID
function getYoutubeVideoId(url) {
  if (!url) return null;
  try {
    if (url.includes('youtube.com/live/')) {
      const match = url.match(/youtube\.com\/live\/([^/?&]+)/);
      if (match && match[1]) return match[1];
    }
    if (url.includes('youtube.com/watch')) {
      const urlObj = new URL(url);
      const v = urlObj.searchParams.get('v');
      if (v) return v;
    }
    if (url.includes('youtu.be/')) {
      const match = url.match(/youtu\.be\/([^/?&]+)/);
      if (match && match[1]) return match[1];
    }
    if (url.includes('youtube.com/embed/')) {
      const match = url.match(/youtube\.com\/embed\/([^/?&]+)/);
      if (match && match[1]) return match[1];
    }
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    if (match && match[2].length === 11) return match[2];
    return null;
  } catch {
    return null;
  }
}

function formatChapterTime(seconds) {
  const totalSeconds = Number(seconds);
  if (!Number.isFinite(totalSeconds) || totalSeconds < 0) return '0:00';
  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${remainingSeconds}`;
}

const PastSessionGroups = ({
  pastSessionGroups,
  formatWorkshopDate,
  isAuthenticated,
  courseRequiresPro, 
  userHasProSubscription, 
  requiredAccessLabel = 'Pro plan',
  upgradePath = '/upgrade-to-pro',
  upgradeCta = 'Upgrade to Pro',
  navigate, // ADD navigate prop
  loginWithRedirect, 
}) => {
  const [visibleCount, setVisibleCount] = useState(1);

  // Helper function to get the latest date for a cohort
  const getLatestDateForCohort = (cohort) => {
    let latestDate = null;

    if (cohort.sessions && cohort.sessions.length > 0) {
      for (const session of cohort.sessions) {
        if (session.scheduledAt) {
          const sessionDate = new Date(session.scheduledAt);
          if (!latestDate || sessionDate > latestDate) {
            latestDate = sessionDate;
          }
        }
      }
    }

    if (!latestDate && cohort.nextSession && cohort.nextSession.scheduledAt) {
      latestDate = new Date(cohort.nextSession.scheduledAt);
    }
    
    return latestDate;
  };

  const sortedPastSessionGroups = useMemo(() => {
    if (!pastSessionGroups) {
      return [];
    }
    return [...pastSessionGroups].sort((a, b) => {
      const aRecordingCount = (a.sessions || []).filter(session => Boolean(session.recordingUrl)).length;
      const bRecordingCount = (b.sessions || []).filter(session => Boolean(session.recordingUrl)).length;
      const aHasRecordings = aRecordingCount > 0;
      const bHasRecordings = bRecordingCount > 0;

      if (aHasRecordings !== bHasRecordings) {
        return aHasRecordings ? -1 : 1;
      }

      if (aRecordingCount !== bRecordingCount) {
        return bRecordingCount - aRecordingCount;
      }

      const dateA = getLatestDateForCohort(a);
      const dateB = getLatestDateForCohort(b);

      if (dateB && dateA) return dateB - dateA; // Sort descending by date
      if (dateB) return 1; // B has a date, A doesn't, B comes first
      if (dateA) return -1; // A has a date, B doesn't, A comes first
      // Fallback to original order or sort by ID if needed, e.g., by cohort ID descending for consistency
      // For now, keep original relative order if dates are missing for both
      return 0; 
    });
  }, [pastSessionGroups]);

  if (!sortedPastSessionGroups || sortedPastSessionGroups.length === 0) {
    return null;
  }

  const handleLoadMore = () => {
    setVisibleCount((prev) => Math.min(prev + 1, sortedPastSessionGroups.length));
  };

  const handleVideoAccess = (session, cohortId) => { 
    pushEvent({ 
        event: ANALYTICS_EVENTS.COURSE_VIDEO_PLAY_ATTEMPT, 
        courseType: session.courseType || 'bootcamp_video', 
        groupId: cohortId, 
        sessionId: session.id,
        isAuthenticated,
        requiresPro: courseRequiresPro,
        hasPro: userHasProSubscription,
    });

    if (courseRequiresPro) {
      if (!isAuthenticated) {
        pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: session?.courseId || 'unknown_course', source: window.location.pathname, contextType: 'view_course_video', itemId: session.id, reason: 'not_authenticated_video_sgs' });
        navigate(upgradePath); // Direct navigation
        return false;
      }
      if (!userHasProSubscription) {
        pushEvent({ event: ANALYTICS_EVENTS.PAYWALL_MODAL_VIEWED, courseId: session?.courseId || 'unknown_course', source: window.location.pathname, contextType: 'view_course_video', itemId: session.id, reason: 'pro_required_video_sgs' });
        navigate(upgradePath);
        return false;
      }
    }
    pushEvent({ 
        event: ANALYTICS_EVENTS.COURSE_VIDEO_PLAY_GRANTED, 
        sessionId: session.id,
        groupId: cohortId, // Use cohortId here
    });
    return true;
  };


  return (
    <section className="edu-past-workshops" id="previously-recorded">
      <h3 className="edu-section-title">Previously Recorded</h3>
      <Row className="edu-past-row">
        {sortedPastSessionGroups.slice(0, visibleCount).map(cohort => {
          const sessions = cohort.sessions || [];
          const isSingleSession = sessions.length === 1;
          return (
            <Col xs={12} key={cohort.id} className="mb-3 edu-past-col">
              <Card className="edu-past-workshop-card" id={`session-group-${cohort.id}`}>
                <Card.Body>
                  {/* Past badge pill */}
                  <div className="edu-past-badge past">Previously Recorded</div>
                  {/* Only show group name/description for multi-session cohorts */}
                  {!isSingleSession && (
                    <>
                      {cohort.name && (
                        <h5 className="edu-past-group-name">{cohort.name}</h5>
                      )}
                      {cohort.nextSession?.scheduledAt && (
                        <p className="edu-past-group-date">Started on: {formatWorkshopDate(cohort.nextSession.scheduledAt)}</p>
                      )}
                      {cohort.description && (
                        <div
                          className="edu-past-group-description"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cohort.description) }}
                        />
                      )}
                      {cohort.longDescription && (
                        <div
                          className="edu-past-group-long-description"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(cohort.longDescription) }}
                        />
                      )}
                    </>
                  )}
                  {sessions.length > 0 && (
                    <div className="edu-past-session-list">
                      {sessions.map((session, index) => {
                        let videoId = null;
                        if (session.recordingUrl) {
                          videoId = getYoutubeVideoId(session.recordingUrl);
                        }
                        return (
                          <div className="edu-past-session-item" key={session.id} id={`session-${session.id}`}>
                            {/* Date first, then title */}
                            {session.scheduledAt && (
                              <div className="edu-past-session-date">
                                <FontAwesomeIcon icon={faCalendarAlt} style={{ marginRight: 8, color: "#ff8c00" }} />
                                {formatWorkshopDate(session.scheduledAt)}
                              </div>
                            )}
                            <div className="edu-past-session-title">
                              {session.title || `Session ${index + 1}`}
                            </div>
                            {session.description && (
                              <div
                                className="edu-past-session-description"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(session.description) }}
                              />
                            )}
                            {session.longDescription && (
                              <div
                                className="edu-past-session-long-description"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(session.longDescription) }}
                              />
                            )}
                            {session.recordingSummary && (
                              <p className="edu-recording-summary">{session.recordingSummary}</p>
                            )}
                            

                            
                            {session.recordingUrl && videoId ? (
                              <div 
                                className="edu-iframe-container" 
                                style={{ aspectRatio: '16/9', cursor: (courseRequiresPro && (!isAuthenticated || !userHasProSubscription)) ? 'pointer' : 'default' }}
                              >
                                {(courseRequiresPro && (!isAuthenticated || !userHasProSubscription)) ? (
                                  <div className="edu-video-paywall-overlay">
                                    <FontAwesomeIcon icon={faLock} size="3x" className="edu-video-paywall-icon"/>
                                    <h4 className="edu-video-paywall-title">Unlock This Recording</h4>
                                    <p className="edu-video-paywall-pitch">
                                      {isAuthenticated 
                                        ? `To watch this recording and access the full library, please get ${requiredAccessLabel}.`
                                        : `Log in and get ${requiredAccessLabel} to watch this video and access the full library.`}
                                    </p>
                                    <Button
                                      className="edu-video-paywall-cta"
                                      onClick={() => handleVideoAccess(session, cohort.id)} 
                                    >
                                      {isAuthenticated ? upgradeCta : 'Login or Subscribe'}
                                      <FontAwesomeIcon icon={faPlayCircle} className="ms-2" />
                                    </Button>
                                  </div>
                                ) : (
                                  <iframe
                                    width="100%"
                                    height="315"
                                    src={`https://www.youtube.com/embed/${videoId}${ (isAuthenticated && userHasProSubscription) || !courseRequiresPro ? '?autoplay=0' : ''}`} // Autoplay only if access granted
                                    title={session.title || `Session ${index + 1}`}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                  ></iframe>
                                )}
                              </div>
                            ) : session.recordingUrl ? (
                              <div className="edu-recording-link-container">
                                <Button
                                  variant="link"
                                  // href is handled by onClick logic if paywalled
                                  target={(courseRequiresPro && (!isAuthenticated || !userHasProSubscription)) ? '_self' : '_blank'}
                                  rel="noopener noreferrer"
                                  className="edu-external-recording-link"
                                  onClick={(e) => {
                                    if (!handleVideoAccess(session, cohort.id)) { 
                                      e.preventDefault(); 
                                    } else {
                                      window.open(session.recordingUrl, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlayCircle} className="me-2" />
                                  Watch Recording 
                                  {courseRequiresPro && (!isAuthenticated || !userHasProSubscription) && <FontAwesomeIcon icon={faLock} className="ms-2 edu-lock-icon-inline" />}
                                </Button>
                                {courseRequiresPro && (!isAuthenticated || !userHasProSubscription) && (
                                  <p className="edu-video-access-pitch edu-video-access-pitch-inline">
                                    Unlock this and all other recordings with {requiredAccessLabel}.
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="edu-no-recording">
                                <p className="edu-recording-coming-soon">
                                  <i className="fas fa-clock me-2"></i>
                                  Recording coming soon
                                </p>
                              </div>
                            )}

                            {/* Course Materials - positioned below video content */}
                            {session.recordingChapters && session.recordingChapters.length > 0 && (
                              <div className="edu-recording-chapters">
                                <h4>Chapters</h4>
                                <div className="edu-recording-chapter-list">
                                  {session.recordingChapters.map((chapter, chapterIndex) => (
                                    <div className="edu-recording-chapter" key={`${session.id}-chapter-${chapterIndex}`}>
                                      <span>{formatChapterTime(chapter.startSeconds)}</span>
                                      <strong>{chapter.title}</strong>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Course Materials - positioned below video content */}
                            {session.materials && session.materials.length > 0 && ((!courseRequiresPro) || (isAuthenticated && userHasProSubscription)) && (
                              <div className="edu-session-materials" style={{ 
                                marginTop: "20px", 
                                padding: "16px", 
                                backgroundColor: "#1a1a1a", 
                                borderRadius: "12px", 
                                border: "1px solid #333333" 
                              }}>
                                <h4 style={{ 
                                  fontSize: "16px", 
                                  fontWeight: "600", 
                                  color: "#ffffff", 
                                  margin: "0 0 12px 0",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px"
                                }}>
                                  📚 Course Materials
                                </h4>
                                <div className="edu-materials-list" style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                                  {session.materials.map((material, materialIndex) => {
                                    const getIconForType = (type) => {
                                      switch (type) {
                                        case 'google_slides': return '📊';
                                        case 'google_docs': return '📄';
                                        case 'google_drive': return '📁';
                                        case 'pdf': return '📕';
                                        case 'youtube': return '🎥';
                                        case 'external_link': return '🔗';
                                        default: return '📎';
                                      }
                                    };
                                    
                                    return (
                                      <a
                                        key={materialIndex}
                                        href={material.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "12px",
                                          padding: "12px 16px",
                                          backgroundColor: "#2a2a2a",
                                          borderRadius: "8px",
                                          border: "1px solid #404040",
                                          textDecoration: "none",
                                          color: "#ffffff",
                                          transition: "all 0.2s ease",
                                          fontSize: "14px"
                                        }}
                                        onMouseEnter={(e) => {
                                          e.target.style.backgroundColor = "#333333";
                                          e.target.style.borderColor = "#555555";
                                        }}
                                        onMouseLeave={(e) => {
                                          e.target.style.backgroundColor = "#2a2a2a";
                                          e.target.style.borderColor = "#404040";
                                        }}
                                      >
                                        <span style={{ fontSize: "18px" }}>{getIconForType(material.type)}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div style={{ 
                                            fontWeight: "500", 
                                            color: "#ffffff",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                            whiteSpace: "nowrap",
                                            marginBottom: material.description ? "4px" : "0"
                                          }}>
                                            {material.title}
                                          </div>
                                          {material.description && (
                                            <div style={{ 
                                              fontSize: "12px", 
                                              color: "#cccccc",
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap"
                                            }}>
                                              {material.description}
                                            </div>
                                          )}
                                        </div>
                                        <FontAwesomeIcon 
                                          icon={faArrowUpRightFromSquare} 
                                          style={{ 
                                            color: "#888888", 
                                            fontSize: "14px" 
                                          }} 
                                        />
                                      </a>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          );
        })}
      </Row>
      {visibleCount < sortedPastSessionGroups.length && (
        <div style={{ textAlign: "center", marginTop: "0.5rem", marginBottom: "2.5rem" }}>
          <Button
            variant="secondary"
            className="edu-past-load-more-btn"
            style={{
              minWidth: 200,
              fontWeight: 600,
              fontSize: "1.08rem",
              borderRadius: 24,
              background: "#444",
              border: "none",
              color: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              transition: "background 0.2s"
            }}
            onClick={handleLoadMore}
          >
            Load more past sessions
          </Button>
        </div>
      )}
    </section>
  );
};

export default PastSessionGroups;
